import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Send, Sparkles, MessageSquare, AlertTriangle, Loader2, BarChart3, Table, TrendingUp, PieChart, LineChart, ScatterChart, Pin, History, Plus, Pencil, Trash2, Check, X, Star, Search, Copy, CheckSquare, Square, Printer,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart as ReLineChart, Line,
  AreaChart, Area,
  PieChart as RePieChart, Pie, Cell,
  ScatterChart as ReScatterChart, Scatter,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit } from '../lib/embedChannel';

/**
 * Embed AI Explore (iframe surface).
 *
 * Mounted at `/embed/v2/explore?token=<jwt>`.
 *
 * A compact chat-style surface a host can drop into their app to give
 * end users a natural-language analytics box. Every submitted question
 * hits `POST /api/embed/v2/explore/ask` — the backend runs the LLM
 * through the workspace's semantic entities and returns a plain-text
 * answer plus an optional chart suggestion. Thread + turn history is
 * persisted server-side so refreshes don't lose context.
 *
 * Emits postMessage events so the host can react to viewer identity,
 * budget exhaustion, and thread creation.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || `authenticate ${res.status}`);
  }
  return res.json();
}

async function askQuestion(token, question, threadId) {
  const res = await fetch(`${API}/api/embed/v2/explore/ask`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, thread_id: threadId || null }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    const err = new Error(detail?.message || `explore ${res.status}`);
    err.status = res.status;
    err.code = detail?.code;
    throw err;
  }
  return res.json();
}

async function loadThreadTurns(token, threadId) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/${encodeURIComponent(threadId)}/turns`,
    { headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) return { data: [] };
  return res.json();
}

async function loadThreadList(token) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads?limit=30`,
    { headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) return { data: [] };
  return res.json();
}

async function renameThread(token, threadId, title) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/${encodeURIComponent(threadId)}`,
    {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `rename ${res.status}`);
  }
  return res.json();
}

async function deleteThread(token, threadId) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/${encodeURIComponent(threadId)}`,
    { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `delete ${res.status}`);
  }
  return res.json();
}

async function pinThread(token, threadId, pin) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/${encodeURIComponent(threadId)}/pin`,
    { method: pin ? 'POST' : 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    const err = new Error(b?.detail?.message || `pin ${res.status}`);
    err.code = b?.detail?.code;
    throw err;
  }
  return res.json();
}

async function bulkDeleteThreads(token, threadIds) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/bulk-delete`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_ids: threadIds }),
    },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `bulk-delete ${res.status}`);
  }
  return res.json();
}

async function bulkPinThreads(token, threadIds, pin) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/threads/bulk-pin`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ thread_ids: threadIds, pin }),
    },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail?.message || `bulk-pin ${res.status}`);
  }
  return res.json();
}

async function listSearchPresets(token) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/search-presets`,
    { headers: { 'Authorization': `Bearer ${token}` } },
  );
  if (!res.ok) return { data: [] };
  return res.json();
}

async function createSearchPreset(token, label, query) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/search-presets`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, query }),
    },
  );
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    const err = new Error(b?.detail?.message || `preset ${res.status}`);
    err.code = b?.detail?.code;
    throw err;
  }
  return res.json();
}

async function deleteSearchPreset(token, presetId) {
  const res = await fetch(
    `${API}/api/embed/v2/explore/search-presets/${encodeURIComponent(presetId)}`,
    { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
  );
  return res.ok;
}

function ChartHint({ chart, token }) {
  const [liveData, setLiveData] = React.useState(null);
  const [liveError, setLiveError] = React.useState(null);
  const [loadingLive, setLoadingLive] = React.useState(false);
  const hasStructuredQuery = !!(chart && chart.structured_query && chart.structured_query.model_id);
  const onLoadLive = React.useCallback(async () => {
    if (!token || !hasStructuredQuery || loadingLive) return;
    setLoadingLive(true);
    setLiveError(null);
    try {
      const sq = chart.structured_query;
      const res = await fetch(`${API}/api/embed/v2/widgets/live-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_id: sq.model_id,
          dimensions: sq.dimensions || [],
          measures: sq.measures || [],
          filters: sq.filters || [],
          limit: 24,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `live-data ${res.status}`);
      }
      const body = await res.json();
      const xKey = chart.x;
      const yKey = chart.y;
      const rows = (body?.rows || [])
        .map((r) => ({ label: r[xKey] ?? r.label, value: Number(r[yKey] ?? r.value) }))
        .filter((r) => Number.isFinite(r.value));
      setLiveData(rows);
      emit('explore.live_data.loaded', { model_id: sq.model_id, row_count: rows.length });
    } catch (e) {
      setLiveError(e.message);
      emit('error', { code: 'live_data_failed', message: e.message });
    } finally {
      setLoadingLive(false);
    }
  }, [token, chart, hasStructuredQuery, loadingLive]);
  if (!chart) return null;
  const iconMap = {
    bar: BarChart3,
    line: LineChart,
    kpi: TrendingUp,
    table: Table,
    pie: PieChart,
    area: LineChart,
    scatter: ScatterChart,
  };
  const Icon = iconMap[chart.type] || BarChart3;
  return (
    <div className="mt-2 rounded-md border border-indigo-500/30 bg-indigo-500/[0.06] p-2" data-testid="embed-explore-chart-hint">
      <div className="flex items-center gap-2 text-[11px]">
        <Icon className="w-3.5 h-3.5 text-indigo-300" />
        <span className="font-semibold text-indigo-200 uppercase tracking-wider">{chart.type}</span>
        {chart.title && <span className="text-muted-foreground">· {chart.title}</span>}
        {hasStructuredQuery && !liveData && (
          <button
            type="button"
            onClick={onLoadLive}
            disabled={loadingLive}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-500/[0.10] hover:bg-emerald-500/[0.20] px-2 py-0.5 text-[9px] font-semibold text-emerald-200 tracking-wide uppercase transition-colors disabled:opacity-40 animate-pulse"
            data-testid="embed-explore-live-data-badge"
            title="This answer can be upgraded to live workspace data"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            {loadingLive ? 'Loading…' : 'Load live data'}
          </button>
        )}
        {liveData && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-500/60 bg-emerald-500/[0.16] px-2 py-0.5 text-[9px] font-semibold text-emerald-100 tracking-wide uppercase" data-testid="embed-explore-live-data-loaded">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Live · {liveData.length} rows
          </span>
        )}
      </div>
      {(chart.x || chart.y || chart.series) && (
        <div className="mt-1 text-[10px] text-muted-foreground font-mono">
          {chart.x && <span>x: {chart.x}</span>}
          {chart.y && <span className="ml-2">y: {chart.y}</span>}
          {chart.series && <span className="ml-2">series: {chart.series}</span>}
        </div>
      )}
      {liveError && (
        <div className="mt-1 text-[10px] text-rose-300" data-testid="embed-explore-live-data-error">
          Live data failed: {liveError}
        </div>
      )}
      <InlineChart chart={chart} overrideData={liveData} />
    </div>
  );
}


/**
 * Inline mini-chart. Renders when the LLM answer carries a `data`
 * array — otherwise falls back to nothing so the pill alone remains.
 *
 * Sized deliberately small (140px tall) so it acts as a preview tile
 * inside the chat bubble, not a full dashboard widget.
 */
const CHART_COLORS = ['#818cf8', '#34d399', '#fbbf24', '#f472b6', '#60a5fa', '#a78bfa'];

function InlineChart({ chart, overrideData }) {
  const data = Array.isArray(overrideData) && overrideData.length > 0
    ? overrideData
    : (Array.isArray(chart?.data) ? chart.data : null);
  if (!data || data.length === 0) return null;
  const type = chart.type;
  if (type === 'kpi') {
    const first = data[0] || {};
    return (
      <div className="mt-2 rounded bg-background/60 border border-border/40 p-3 text-center" data-testid="embed-explore-chart-render">
        <div className="text-2xl font-bold text-indigo-200 font-mono">
          {typeof first.value === 'number' ? first.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(first.value ?? '')}
        </div>
        {first.label && <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{first.label}</div>}
      </div>
    );
  }
  if (type === 'table') {
    return (
      <div className="mt-2 rounded bg-background/60 border border-border/40 overflow-hidden" data-testid="embed-explore-chart-render">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left px-2 py-1 font-mono text-muted-foreground">{chart.x || 'label'}</th>
              <th className="text-right px-2 py-1 font-mono text-muted-foreground">{chart.y || 'value'}</th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 8).map((row, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="px-2 py-1">{row.label}</td>
                <td className="px-2 py-1 text-right font-mono">{typeof row.value === 'number' ? row.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const axisProps = {
    stroke: '#94a3b8', tick: { fontSize: 9, fill: '#94a3b8' },
    axisLine: { stroke: '#334155' }, tickLine: { stroke: '#334155' },
  };
  const tooltipStyle = {
    contentStyle: { background: '#0f172a', border: '1px solid #334155', fontSize: 10, borderRadius: 6 },
    labelStyle: { color: '#e2e8f0' },
    itemStyle: { color: '#e2e8f0' },
  };
  const commonWrapper = (children) => (
    <div className="mt-2 rounded bg-background/60 border border-border/40 p-1" data-testid="embed-explore-chart-render">
      <ResponsiveContainer width="100%" height={140}>
        {children}
      </ResponsiveContainer>
    </div>
  );
  if (type === 'bar') {
    return commonWrapper(
      <BarChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip {...tooltipStyle} />
        <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
      </BarChart>
    );
  }
  if (type === 'line') {
    return commonWrapper(
      <ReLineChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip {...tooltipStyle} />
        <Line type="monotone" dataKey="value" stroke={CHART_COLORS[1]} strokeWidth={2} dot={{ fill: CHART_COLORS[1], r: 3 }} />
      </ReLineChart>
    );
  }
  if (type === 'area') {
    return commonWrapper(
      <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip {...tooltipStyle} />
        <Area type="monotone" dataKey="value" stroke={CHART_COLORS[4]} fill={CHART_COLORS[4]} fillOpacity={0.35} />
      </AreaChart>
    );
  }
  if (type === 'pie') {
    return commonWrapper(
      <RePieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <Tooltip {...tooltipStyle} />
        <Pie data={data} dataKey="value" nameKey="label" innerRadius={30} outerRadius={55}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
      </RePieChart>
    );
  }
  if (type === 'scatter') {
    return commonWrapper(
      <ReScatterChart margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" {...axisProps} />
        <YAxis dataKey="value" {...axisProps} />
        <Tooltip {...tooltipStyle} />
        <Scatter data={data} fill={CHART_COLORS[5]} />
      </ReScatterChart>
    );
  }
  return null;
}

function SqlBlock({ sql }) {
  if (!sql) return null;
  return (
    <details className="mt-2" data-testid="embed-explore-sql">
      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-indigo-300">
        Query
      </summary>
      <pre className="mt-1 rounded-md border border-border/40 bg-muted/30 p-2 text-[10px] font-mono whitespace-pre-wrap break-all">
        {sql}
      </pre>
    </details>
  );
}

function TurnBubble({ turn, onPin, dashboards, token }) {
  const isUser = turn.role === 'user';
  const canPin = !isUser && !!turn.chart && !!onPin && (dashboards || []).length > 0;
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pinning, setPinning] = React.useState(false);
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={`embed-explore-turn-${turn.turn_id}`}
    >
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-[12px] ${
          isUser
            ? 'bg-indigo-500 text-white'
            : 'border border-border/40 bg-background/60 text-foreground'
        }`}
      >
        <div className="whitespace-pre-wrap break-words">{turn.content}</div>
        {!isUser && Array.isArray(turn.provenance) && turn.provenance.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`embed-provenance-${turn.turn_id}`}>
            {turn.provenance.map((p) => {
              const style = p.kind === 'certified'
                ? { border: '1px solid rgba(16,185,129,0.5)', color: '#6ee7b7', background: 'rgba(16,185,129,0.12)' }
                : p.kind === 'uncertified'
                  ? { border: '1px solid rgba(245,158,11,0.5)', color: '#fcd34d', background: 'rgba(245,158,11,0.12)' }
                  : { border: '1px solid rgba(56,189,248,0.5)', color: '#7dd3fc', background: 'rgba(56,189,248,0.12)' };
              const label = p.kind === 'certified' ? 'Certified' : p.kind === 'uncertified' ? 'Uncertified' : 'AI calculation';
              return (
                <span key={p.name} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px]"
                  style={style} title={p.kind === 'certified' ? 'Peer-reviewed governed measure' : p.kind === 'uncertified' ? 'Governed but not yet certified' : 'Computed by AI — not a governed measure'}
                  data-testid={`embed-provenance-tag-${p.name}`}>
                  {label}: <span className="font-mono">{p.label || p.name}</span>
                </span>
              );
            })}
          </div>
        )}
        {!isUser && <ChartHint chart={turn.chart} token={token} />}
        {!isUser && <SqlBlock sql={turn.sql} />}
        {canPin && (
          <div className="mt-2 relative" data-testid={`embed-explore-pin-block-${turn.turn_id}`}>
            <button
              type="button"
              onClick={() => setPinOpen((o) => !o)}
              disabled={pinning}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-500/[0.08] hover:bg-indigo-500/[0.16] px-2 py-1 text-[10px] text-indigo-200 transition-colors disabled:opacity-40"
              data-testid={`embed-explore-pin-toggle-${turn.turn_id}`}
            >
              <Pin className="w-3 h-3" />
              {pinning ? 'Pinning…' : 'Pin to dashboard'}
            </button>
            {pinOpen && (
              <div className="absolute z-10 top-full mt-1 left-0 w-64 max-h-56 overflow-auto rounded-md border border-border/40 bg-background shadow-xl" data-testid={`embed-explore-pin-list-${turn.turn_id}`}>
                {dashboards.map((d) => (
                  <button
                    key={d.dashboard_id}
                    type="button"
                    onClick={async () => {
                      setPinning(true);
                      try {
                        await onPin(turn.turn_id, d.dashboard_id);
                        setPinOpen(false);
                      } finally {
                        setPinning(false);
                      }
                    }}
                    className="block w-full text-left px-3 py-2 text-[11px] hover:bg-indigo-500/[0.12] border-b border-border/30 last:border-0"
                    data-testid={`embed-explore-pin-target-${turn.turn_id}-${d.dashboard_id}`}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadRow({ thread, active, selectMode, selected, onSelect, onOpen, onRename, onDelete, onTogglePin }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(thread.title || '');
  const [saving, setSaving] = React.useState(false);
  const [confirmDel, setConfirmDel] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const submit = async () => {
    const clean = (draft || '').trim();
    if (!clean || clean === thread.title) {
      setEditing(false);
      setDraft(thread.title || '');
      return;
    }
    setSaving(true);
    try {
      await onRename(clean);
      setEditing(false);
    } catch (e) { /* toast handled upstream */ }
    finally { setSaving(false); }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(thread.title || '');
  };

  const doDelete = async (e) => {
    e.stopPropagation();
    if (!confirmDel) {
      setConfirmDel(true);
      setTimeout(() => setConfirmDel(false), 3000);
      return;
    }
    await onDelete();
  };

  return (
    <div
      className={`group relative border-b border-border/30 last:border-0 hover:bg-indigo-500/[0.10] ${
        active ? 'bg-indigo-500/[0.16]' : ''
      } ${selected ? 'bg-emerald-500/[0.12]' : ''}`}
      data-testid={`embed-explore-history-thread-${thread.thread_id}`}
    >
      {selectMode && !editing && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect?.(); }}
          className="absolute left-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-emerald-300"
          data-testid={`embed-explore-history-select-${thread.thread_id}`}
          aria-label="Toggle thread selection"
        >
          {selected
            ? <CheckSquare className="w-3.5 h-3.5 text-emerald-300" />
            : <Square className="w-3.5 h-3.5" />}
        </button>
      )}
      {editing ? (
        <div className="flex items-center gap-1 px-2 py-2" onClick={(e) => e.stopPropagation()}>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            disabled={saving}
            maxLength={120}
            className="flex-1 min-w-0 rounded border border-indigo-500/40 bg-background px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-400/60"
            data-testid={`embed-explore-history-rename-input-${thread.thread_id}`}
          />
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded p-1 text-emerald-300 hover:bg-emerald-500/[0.16] disabled:opacity-40"
            data-testid={`embed-explore-history-rename-save-${thread.thread_id}`}
            title="Save"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="rounded p-1 text-muted-foreground hover:bg-muted/40"
            data-testid={`embed-explore-history-rename-cancel-${thread.thread_id}`}
            title="Cancel"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={selectMode ? onSelect : onOpen}
            className={`block w-full text-left py-2 pr-16 ${selectMode ? 'pl-8' : 'px-3'}`}
            data-testid={`embed-explore-history-open-${thread.thread_id}`}
          >
            <div className="text-[11px] font-medium text-foreground truncate">
              {thread.title || thread.first_question || 'Untitled thread'}
            </div>
            <div className="text-[9px] text-muted-foreground font-mono flex items-center justify-between mt-0.5">
              <span>{(thread.updated_at || thread.created_at || '').slice(0, 16)}</span>
              <span>{thread.turn_count ? `${thread.turn_count} turns` : ''}</span>
            </div>
          </button>
          <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onTogglePin?.(!thread.pinned); }}
              className={`rounded p-1 transition-opacity ${
                thread.pinned
                  ? 'text-amber-300 opacity-100'
                  : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-muted/60 hover:text-amber-300'
              }`}
              data-testid={`embed-explore-history-pin-${thread.thread_id}`}
              title={thread.pinned ? 'Unpin thread' : 'Pin to top (max 3)'}
            >
              <Star className={`w-3 h-3 ${thread.pinned ? 'fill-current' : ''}`} />
            </button>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                className="rounded p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                data-testid={`embed-explore-history-rename-${thread.thread_id}`}
                title="Rename thread"
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={doDelete}
                className={`rounded p-1 hover:bg-rose-500/[0.16] ${
                  confirmDel ? 'text-rose-300 bg-rose-500/[0.10]' : 'text-muted-foreground hover:text-rose-300'
                }`}
                data-testid={`embed-explore-history-delete-${thread.thread_id}`}
                title={confirmDel ? 'Click again to confirm delete' : 'Delete thread'}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function EmbedExplorePage() {
  const token = useEmbedToken();
  const [claims, setClaims] = useState(null);
  const [branding, setBranding] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [starters, setStarters] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [pinToast, setPinToast] = useState(null);
  const [threads, setThreads] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [threadSearch, setThreadSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchPresets, setSearchPresets] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authenticate(token)
      .then((c) => {
        if (cancelled) return;
        setClaims(c);
        setBranding(c?.branding || null);
        emit('viewer.identified', { sub: c?.sub, roles: c?.roles || [], capabilities: c?.capabilities || [] });
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    // Best-effort load starter questions in parallel with auth.
    fetch(`${API}/api/embed/v2/explore/starter-questions`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (!cancelled && b?.data) setStarters(b.data); })
      .catch(() => { /* silent */ });
    // Load workspace dashboards so we can offer "Pin to..." on
    // assistant answers.
    fetch(`${API}/api/embed/v2/dashboards?limit=50`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (cancelled || !b?.data) return;
        setDashboards(b.data.map((d) => ({
          dashboard_id: d.dashboard_id,
          name: d.name || d.dashboard_id,
        })));
      })
      .catch(() => { /* silent */ });
    // Load past threads for the history sidebar. Best-effort — a new
    // viewer with no prior threads gets an empty sidebar.
    loadThreadList(token)
      .then((b) => { if (!cancelled && b?.data) setThreads(b.data); })
      .catch(() => { /* silent */ });
    // Load saved search-preset chips.
    listSearchPresets(token)
      .then((b) => { if (!cancelled && b?.data) setSearchPresets(b.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token]);

  // Auto-scroll on new turns.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length]);

  const submitText = useCallback(async (rawText) => {
    const q = (rawText || '').trim();
    if (!q || pending || !token) return;
    setPending(true);
    setError(null);
    const optimistic = {
      turn_id: `local_${Date.now()}`,
      role: 'user',
      content: q,
    };
    setTurns((prev) => [...prev, optimistic]);
    setQuestion('');
    try {
      const result = await askQuestion(token, q, threadId);
      if (!threadId && result.thread_id) {
        setThreadId(result.thread_id);
        emit('explore.thread.created', { thread_id: result.thread_id });
        const t = await loadThreadTurns(token, result.thread_id);
        setTurns(t.data || []);
      } else {
        setTurns((prev) => {
          const withoutOptimistic = prev.filter((tn) => tn.turn_id !== optimistic.turn_id);
          return [...withoutOptimistic, ...(result.history || [])];
        });
      }
      emit('explore.turn.answered', { thread_id: result.thread_id, turn_id: result.turn?.turn_id });
    } catch (e) {
      setError(e.message);
      setTurns((prev) => prev.filter((tn) => tn.turn_id !== optimistic.turn_id));
      if (e.code === 'budget_exhausted') {
        emit('budget.exhausted', { kind: 'ai', message: e.message });
      } else {
        emit('error', { code: e.code || 'unknown', message: e.message });
      }
    } finally {
      setPending(false);
    }
  }, [pending, token, threadId]);

  const submit = useCallback(() => submitText(question), [submitText, question]);

  const openThread = useCallback(async (nextThreadId) => {
    if (!token || pending) return;
    setThreadId(nextThreadId);
    setHistoryOpen(false);
    setError(null);
    try {
      const b = await loadThreadTurns(token, nextThreadId);
      setTurns(b.data || []);
      emit('explore.thread.opened', { thread_id: nextThreadId });
    } catch (e) {
      setError(e.message);
    }
  }, [token, pending]);

  const startNewThread = useCallback(() => {
    if (pending) return;
    setThreadId(null);
    setTurns([]);
    setHistoryOpen(false);
    setError(null);
    setQuestion('');
    emit('explore.thread.started', {});
  }, [pending]);

  const onRenameThread = useCallback(async (id, title) => {
    if (!token) return;
    try {
      const updated = await renameThread(token, id, title);
      setThreads((prev) => prev.map((t) => (t.thread_id === id ? { ...t, ...updated } : t)));
      emit('explore.thread.renamed', { thread_id: id, title });
    } catch (e) {
      setPinToast(`Rename failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 3500);
    }
  }, [token]);

  const onDeleteThread = useCallback(async (id) => {
    if (!token) return;
    try {
      await deleteThread(token, id);
      setThreads((prev) => prev.filter((t) => t.thread_id !== id));
      if (threadId === id) {
        setThreadId(null);
        setTurns([]);
      }
      emit('explore.thread.deleted', { thread_id: id });
    } catch (e) {
      setPinToast(`Delete failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 3500);
    }
  }, [token, threadId]);

  const onTogglePin = useCallback(async (id, pin) => {
    if (!token) return;
    try {
      const updated = await pinThread(token, id, pin);
      setThreads((prev) => {
        // Replace + re-sort so pinned threads float to the top and
        // the star position updates immediately.
        const next = prev.map((t) => (t.thread_id === id ? { ...t, ...updated } : t));
        next.sort((a, b) => {
          if (!!b.pinned - !!a.pinned) return !!b.pinned - !!a.pinned;
          return (b.updated_at || '').localeCompare(a.updated_at || '');
        });
        return next;
      });
      emit(pin ? 'explore.thread.pinned' : 'explore.thread.unpinned', { thread_id: id });
    } catch (e) {
      const msg = e.code === 'pin_cap_reached'
        ? 'Pin limit reached (3). Unpin another thread first.'
        : `Pin failed: ${e.message}`;
      setPinToast(msg);
      setTimeout(() => setPinToast(null), 4000);
    }
  }, [token]);

  const filteredThreads = React.useMemo(() => {
    const q = (threadSearch || '').trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.title || '').toLowerCase().includes(q));
  }, [threads, threadSearch]);

  const onSaveSearchPreset = useCallback(async () => {
    if (!token) return;
    const clean = (threadSearch || '').trim();
    if (!clean) return;
    const label = window.prompt('Name this search chip:', clean.slice(0, 40));
    if (!label) return;
    try {
      const row = await createSearchPreset(token, label, clean);
      setSearchPresets((prev) => {
        // Dedupe on preset_id — the server returns the existing row
        // when the label already exists.
        const next = prev.filter((p) => p.preset_id !== row.preset_id);
        next.push(row);
        return next;
      });
      emit('explore.search_preset.saved', { preset_id: row.preset_id });
    } catch (e) {
      const msg = e.code === 'preset_cap_reached'
        ? 'Preset limit reached (12). Delete another first.'
        : `Save failed: ${e.message}`;
      setPinToast(msg);
      setTimeout(() => setPinToast(null), 4000);
    }
  }, [token, threadSearch]);

  const onDeleteSearchPreset = useCallback(async (presetId) => {
    if (!token) return;
    const ok = await deleteSearchPreset(token, presetId);
    if (ok) setSearchPresets((prev) => prev.filter((p) => p.preset_id !== presetId));
  }, [token]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const bulkDelete = useCallback(async () => {
    if (!token || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (!window.confirm(`Delete ${ids.length} thread${ids.length === 1 ? '' : 's'} and their turns? This cannot be undone.`)) {
      return;
    }
    try {
      const b = await bulkDeleteThreads(token, ids);
      const deleted = new Set(b.deleted || []);
      setThreads((prev) => prev.filter((t) => !deleted.has(t.thread_id)));
      if (threadId && deleted.has(threadId)) {
        setThreadId(null);
        setTurns([]);
      }
      clearSelection();
      emit('explore.threads.bulk_deleted', { count: b.deleted_count });
    } catch (e) {
      setPinToast(`Bulk delete failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 3500);
    }
  }, [token, selectedIds, threadId, clearSelection]);

  const bulkPin = useCallback(async (pin) => {
    if (!token || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      const b = await bulkPinThreads(token, ids, pin);
      // Merge server state back into the local list.
      const updated = new Set(b.updated || []);
      setThreads((prev) => {
        const next = prev.map((t) =>
          updated.has(t.thread_id) ? { ...t, pinned: pin } : t,
        );
        next.sort((a, x) => {
          if (!!x.pinned - !!a.pinned) return !!x.pinned - !!a.pinned;
          return (x.updated_at || '').localeCompare(a.updated_at || '');
        });
        return next;
      });
      if (pin && (b.over_cap || []).length > 0) {
        setPinToast(`Pinned ${b.updated_count}. ${b.over_cap.length} skipped — pin cap is 3.`);
        setTimeout(() => setPinToast(null), 4500);
      }
      clearSelection();
      emit(pin ? 'explore.threads.bulk_pinned' : 'explore.threads.bulk_unpinned', {
        count: b.updated_count, over_cap: (b.over_cap || []).length,
      });
    } catch (e) {
      setPinToast(`Bulk ${pin ? 'pin' : 'unpin'} failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 3500);
    }
  }, [token, selectedIds, clearSelection]);

  const [transcriptCopied, setTranscriptCopied] = useState(false);

  // Build the same markdown structure used by copyTranscript, but as
  // structured turns for the print HTML layout.
  const printThread = useCallback(() => {
    if (!turns || turns.length === 0) return;
    const currentThread = threads.find((t) => t.thread_id === threadId);
    const heading = currentThread?.title || 'Explore transcript';
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (ch) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]
    ));
    const body = turns.map((t) => {
      const who = t.role === 'user' ? 'You' : 'Assistant';
      const roleTone = t.role === 'user' ? '#3730a3' : '#065f46';
      let chartBlock = '';
      if (t.chart && Array.isArray(t.chart.data) && t.chart.data.length > 0) {
        const xLabel = esc(t.chart.x || 'label');
        const yLabel = esc(t.chart.y || 'value');
        const rows = t.chart.data.slice(0, 40).map(
          (r) => `<tr><td>${esc(r.label)}</td><td style="text-align:right">${esc(r.value)}</td></tr>`,
        ).join('');
        chartBlock = `
          <table class="chart">
            <thead><tr><th>${xLabel}</th><th style="text-align:right">${yLabel}</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>`;
      }
      const sqlBlock = t.sql ? `<pre class="sql">${esc(t.sql)}</pre>` : '';
      return `
        <section class="turn">
          <div class="role" style="color:${roleTone}">${who}</div>
          <div class="content">${esc(t.content || '')}</div>
          ${chartBlock}
          ${sqlBlock}
        </section>`;
    }).join('\n');
    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(heading)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; margin: 32px; line-height: 1.5; }
  h1 { font-size: 20px; margin-bottom: 4px; border-bottom: 1px solid #cbd5e1; padding-bottom: 8px; }
  .meta { color: #64748b; font-size: 12px; margin-bottom: 24px; }
  .turn { margin-bottom: 20px; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 6px; page-break-inside: avoid; }
  .role { font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
  .content { white-space: pre-wrap; font-size: 13px; }
  table.chart { margin-top: 10px; border-collapse: collapse; font-size: 12px; width: 100%; }
  table.chart th, table.chart td { border: 1px solid #e2e8f0; padding: 4px 8px; }
  table.chart th { background: #f1f5f9; text-align: left; }
  pre.sql { margin-top: 10px; background: #0f172a; color: #e2e8f0; padding: 10px; border-radius: 4px; font-size: 11px; overflow-x: auto; white-space: pre-wrap; }
  @media print { body { margin: 24px; } }
</style></head><body>
  <h1>${esc(heading)}</h1>
  <div class="meta">${esc(new Date().toLocaleString())} · ${turns.length} turns</div>
  ${body}
</body></html>`;
    // Open a same-origin blob URL — no popup-blocker issues, no
    // window.opener leak. `window.print()` fires once the new tab
    // renders.
    const win = window.open('', '_blank', 'noopener,noreferrer');
    if (!win) {
      setPinToast('Enable pop-ups to print the transcript.');
      setTimeout(() => setPinToast(null), 3500);
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* ignore */ } }, 250);
    emit('explore.transcript.printed', { thread_id: threadId, turn_count: turns.length });
  }, [turns, threads, threadId]);
  const copyTranscript = useCallback(async () => {
    // Build a markdown transcript client-side so hosts don't need a
    // roundtrip. Charts render as compact tables; SQL blocks land as
    // fenced code so the paste target (Slack, Notion, email) keeps
    // formatting.
    if (!turns || turns.length === 0) return;
    const lines = [];
    const currentThread = threads.find((t) => t.thread_id === threadId);
    if (currentThread?.title) lines.push(`# ${currentThread.title}`, '');
    for (const t of turns) {
      const who = t.role === 'user' ? '**You**' : '**Assistant**';
      lines.push(who);
      lines.push('');
      lines.push((t.content || '').trim());
      lines.push('');
      if (t.chart && Array.isArray(t.chart.data) && t.chart.data.length > 0) {
        const rows = t.chart.data.slice(0, 20);
        const xLabel = t.chart.x || 'label';
        const yLabel = t.chart.y || 'value';
        lines.push(`| ${xLabel} | ${yLabel} |`);
        lines.push(`| --- | ---: |`);
        for (const r of rows) {
          lines.push(`| ${String(r.label ?? '').replace(/\|/g, '\\|')} | ${r.value ?? ''} |`);
        }
        lines.push('');
      }
      if (t.sql) {
        lines.push('```sql');
        lines.push(t.sql);
        lines.push('```');
        lines.push('');
      }
    }
    const md = lines.join('\n').trim() + '\n';
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(md);
      } else {
        // Fallback for older browsers / iframe environments where
        // clipboard permission is denied — stash into a hidden
        // textarea and use the legacy exec command.
        const ta = document.createElement('textarea');
        ta.value = md;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setTranscriptCopied(true);
      emit('explore.transcript.copied', { thread_id: threadId, turn_count: turns.length });
      setTimeout(() => setTranscriptCopied(false), 2000);
    } catch (e) {
      setPinToast(`Copy failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 3500);
    }
  }, [turns, threads, threadId]);


  // Refresh the thread list after a new answer lands so the sidebar
  // shows the latest turn text on the top row.
  useEffect(() => {
    if (!token || !threadId) return;
    loadThreadList(token)
      .then((b) => { if (b?.data) setThreads(b.data); })
      .catch(() => { /* silent */ });
  }, [token, threadId, turns.length]);

  const canWrite = (claims?.capabilities || []).includes('write');
  const pinTurn = useCallback(async (turnId, dashboardId) => {
    if (!token || !canWrite) return;
    try {
      const res = await fetch(`${API}/api/embed/v2/explore/turns/${encodeURIComponent(turnId)}/pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dashboard_id: dashboardId }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `pin ${res.status}`);
      }
      const body = await res.json();
      setPinToast(`Pinned to ${dashboards.find((d) => d.dashboard_id === dashboardId)?.name || dashboardId}`);
      emit('explore.turn.pinned', { turn_id: turnId, dashboard_id: dashboardId, widget_id: body?.widget?.widget_id });
      // Auto-dismiss the toast after 3s.
      setTimeout(() => setPinToast(null), 3000);
    } catch (e) {
      setPinToast(`Pin failed: ${e.message}`);
      setTimeout(() => setPinToast(null), 4000);
    }
  }, [token, canWrite, dashboards]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="embed-explore-missing-token">
        Waiting for session token…
      </div>
    );
  }

  const productName = brandingProductName(branding);

  return (
    <div
      className="min-h-screen flex flex-col bg-background text-foreground"
      style={brandingRootStyle(branding)}
      data-testid="embed-explore-root"
    >
      <header className="px-4 py-3 border-b border-border/40 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-indigo-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="embed-explore-product-name">
              {productName || 'AI Explore'}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              Ask a question about your data
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={startNewThread}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded-md border border-border/40 hover:bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground disabled:opacity-40"
            data-testid="embed-explore-new-thread"
            title="Start a fresh question thread"
          >
            <Plus className="w-3 h-3" /> New
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="inline-flex items-center gap-1 rounded-md border border-border/40 hover:bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
              data-testid="embed-explore-history-toggle"
              title="Open past question threads"
            >
              <History className="w-3 h-3" /> History
              {threads.length > 0 && (
                <span className="ml-0.5 rounded-full bg-indigo-500/[0.16] text-indigo-200 px-1.5 text-[9px]">{threads.length}</span>
              )}
            </button>
            {historyOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-hidden flex flex-col rounded-md border border-border/40 bg-background shadow-xl z-20"
                data-testid="embed-explore-history-panel"
              >
                <div className="px-2 py-1 border-b border-border/30 flex items-center justify-between gap-2 text-[10px] text-muted-foreground" data-testid="embed-explore-history-toolbar">
                  <button
                    type="button"
                    onClick={() => {
                      if (selectMode) clearSelection();
                      else { setSelectMode(true); setSelectedIds(new Set()); }
                    }}
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted/40 ${
                      selectMode ? 'text-emerald-300' : ''
                    }`}
                    data-testid="embed-explore-history-select-toggle"
                  >
                    {selectMode ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                    {selectMode ? `Selected ${selectedIds.size}` : 'Select'}
                  </button>
                  {selectMode && selectedIds.size > 0 && (
                    <div className="flex items-center gap-1" data-testid="embed-explore-history-bulk-actions">
                      <button
                        type="button"
                        onClick={() => bulkPin(true)}
                        className="rounded p-1 hover:bg-amber-500/[0.16] text-amber-300"
                        data-testid="embed-explore-history-bulk-pin"
                        title="Pin selected"
                      >
                        <Star className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => bulkPin(false)}
                        className="rounded p-1 hover:bg-muted/40 text-muted-foreground"
                        data-testid="embed-explore-history-bulk-unpin"
                        title="Unpin selected"
                      >
                        <Star className="w-3 h-3 opacity-40" />
                      </button>
                      <button
                        type="button"
                        onClick={bulkDelete}
                        className="rounded p-1 hover:bg-rose-500/[0.16] text-rose-300"
                        data-testid="embed-explore-history-bulk-delete"
                        title="Delete selected"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                {threads.length > 3 && !selectMode && (
                  <div className="px-2 py-1.5 border-b border-border/30 flex items-center gap-1.5" data-testid="embed-explore-history-search-wrap">
                    <Search className="w-3 h-3 text-muted-foreground shrink-0" />
                    <input
                      type="text"
                      value={threadSearch}
                      onChange={(e) => setThreadSearch(e.target.value)}
                      placeholder="Filter threads…"
                      maxLength={80}
                      className="flex-1 min-w-0 bg-transparent text-[11px] focus:outline-none placeholder:text-muted-foreground/80"
                      data-testid="embed-explore-history-search"
                    />
                    {threadSearch && (
                      <>
                        <button
                          type="button"
                          onClick={onSaveSearchPreset}
                          className="rounded p-0.5 text-emerald-300 hover:bg-emerald-500/[0.16]"
                          data-testid="embed-explore-history-search-save"
                          title="Save this filter as a preset chip"
                        >
                          <Star className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setThreadSearch('')}
                          className="rounded p-0.5 text-muted-foreground hover:bg-muted/40"
                          data-testid="embed-explore-history-search-clear"
                          title="Clear filter"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}
                {searchPresets.length > 0 && !selectMode && (
                  <div className="px-2 py-1 border-b border-border/30 flex flex-wrap gap-1" data-testid="embed-explore-history-preset-chips">
                    {searchPresets.map((p) => (
                      <div
                        key={p.preset_id}
                        className={`group inline-flex items-center gap-0.5 rounded-full border text-[9px] transition-colors ${
                          threadSearch === p.query
                            ? 'border-emerald-500/60 bg-emerald-500/[0.16] text-emerald-100'
                            : 'border-border/40 bg-background/50 hover:border-indigo-400/40 text-muted-foreground'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setThreadSearch(p.query)}
                          className="px-2 py-0.5"
                          data-testid={`embed-explore-history-preset-chip-${p.preset_id}`}
                          title={`Filter: ${p.query}`}
                        >
                          {p.label}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteSearchPreset(p.preset_id)}
                          className="pr-1.5 opacity-0 group-hover:opacity-100 hover:text-rose-300"
                          data-testid={`embed-explore-history-preset-chip-delete-${p.preset_id}`}
                          title="Delete chip"
                          aria-label="Delete search preset"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="overflow-auto flex-1">
                {threads.length === 0 ? (
                  <div className="p-3 text-[11px] text-muted-foreground italic" data-testid="embed-explore-history-empty">
                    No past threads yet — ask a question to start one.
                  </div>
                ) : filteredThreads.length === 0 ? (
                  <div className="p-3 text-[11px] text-muted-foreground italic" data-testid="embed-explore-history-search-empty">
                    No threads match &quot;{threadSearch}&quot;
                  </div>
                ) : filteredThreads.map((t) => (
                  <ThreadRow
                    key={t.thread_id}
                    thread={t}
                    active={t.thread_id === threadId}
                    selectMode={selectMode}
                    selected={selectedIds.has(t.thread_id)}
                    onSelect={() => toggleSelect(t.thread_id)}
                    onOpen={() => openThread(t.thread_id)}
                    onRename={(title) => onRenameThread(t.thread_id, title)}
                    onDelete={() => onDeleteThread(t.thread_id)}
                    onTogglePin={(pin) => onTogglePin(t.thread_id, pin)}
                  />
                ))}
                </div>
              </div>
            )}
          </div>
          {claims && (
            <div className="text-[10px] text-muted-foreground text-right" data-testid="embed-explore-viewer">
              <div className="font-mono">{claims.sub}</div>
              <div>{(claims.capabilities || ['read']).join(', ')}</div>
            </div>
          )}
        </div>
      </header>

      <div
        ref={scrollerRef}
        className="flex-1 overflow-auto px-4 py-3 space-y-2"
        data-testid="embed-explore-scroller"
      >
        {turns.length === 0 && !pending && (
          <div className="text-center text-[11px] text-muted-foreground italic mt-8 space-y-4" data-testid="embed-explore-empty">
            <div>
              <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" />
              Ask a question to get started.
            </div>
            {starters.length > 0 && (
              <div className="not-italic" data-testid="embed-explore-starters">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                  Suggested prompts
                </div>
                <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
                  {starters.map((s) => (
                    <button
                      key={s.starter_id}
                      type="button"
                      onClick={() => submitText(s.text)}
                      className="rounded-full border border-indigo-500/40 bg-indigo-500/[0.08] hover:bg-indigo-500/[0.16] px-3 py-1.5 text-[11px] text-indigo-100 transition-colors"
                      data-testid={`embed-explore-starter-${s.starter_id}`}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {turns.map((t) => (
          <TurnBubble
            key={t.turn_id}
            turn={t}
            onPin={canWrite ? pinTurn : null}
            dashboards={dashboards}
            token={token}
          />
        ))}
        {pending && (
          <div className="flex justify-start" data-testid="embed-explore-pending">
            <div className="max-w-[85%] rounded-lg px-3 py-2 text-[12px] border border-border/40 bg-background/60">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline-block mr-1.5" />
              Thinking…
            </div>
          </div>
        )}
        {turns.length > 0 && !pending && (
          <div className="pt-2 flex justify-center gap-2" data-testid="embed-explore-transcript-actions">
            <button
              type="button"
              onClick={copyTranscript}
              className="inline-flex items-center gap-1 rounded-md border border-border/40 hover:bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
              data-testid="embed-explore-copy-transcript"
              title="Copy the full user/assistant transcript to the clipboard as markdown"
            >
              {transcriptCopied
                ? <><Check className="w-3 h-3 text-emerald-300" /> Copied!</>
                : <><Copy className="w-3 h-3" /> Copy transcript</>}
            </button>
            <button
              type="button"
              onClick={printThread}
              className="inline-flex items-center gap-1 rounded-md border border-border/40 hover:bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
              data-testid="embed-explore-print-transcript"
              title="Open a print-friendly HTML view of this thread"
            >
              <Printer className="w-3 h-3" /> Print
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-4 mb-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 flex items-start gap-2" data-testid="embed-explore-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {pinToast && (
        <div className="mx-4 mb-2 rounded-md border border-indigo-500/40 bg-indigo-500/[0.12] px-3 py-2 text-[11px] text-indigo-100 flex items-center gap-2" data-testid="embed-explore-pin-toast">
          <Pin className="w-3.5 h-3.5 shrink-0" />
          <div>{pinToast}</div>
        </div>
      )}

      <footer className="px-4 py-3 border-t border-border/40">
        <div className="flex items-end gap-2">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about your data…"
            rows={1}
            className="flex-1 min-w-0 resize-none bg-transparent border border-border/40 rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-indigo-400/60"
            data-testid="embed-explore-input"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!question.trim() || pending}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="embed-explore-send"
          >
            <Send className="w-3.5 h-3.5" /> Ask
          </button>
        </div>
      </footer>
    </div>
  );
}
